import axios from "axios";
import { ElMessage } from "element-plus";
import router from "@/router";

/**
 * 简化的Token处理工具函数
 */
const tokenUtils = {
    /**
     * 获取指定名称的Cookie值
     * @param {string} name - Cookie名称
     * @returns {string|null} Cookie值或null
     */
    getCookie(name) {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) {
            return parts.pop().split(";").shift();
        }
        return null;
    },

    /**
     * 删除Cookie
     * @param {string} name - Cookie名称
     * @param {string} path - Cookie路径
     */
    deleteCookie(name, path = "/") {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}`;
    },
};

/**
 * 创建axios实例
 * @param {Object} config - 自定义配置
 * @returns {Object} axios实例
 */
const createRequest = (config = {}) => {
    const defaultConfig = {
        baseURL: import.meta.env.VITE_API_BASE_URL,
        timeout: 60000, // 60秒
        headers: {
            "Content-Type": "application/json",
            "x-from": "openapi-platform",
        },
    };

    const instance = axios.create({ ...defaultConfig, ...config });

    // 请求拦截器 - 处理Token传输
    instance.interceptors.request.use(
        (config) => {
            const satoken = tokenUtils.getCookie("satoken");
            if (satoken) {
                config.headers["satoken"] = satoken;
            }

            return config;
        },
        (error) => {
            console.error("请求拦截器错误:", error);
            return Promise.reject(error);
        },
    );

    // 响应拦截器
    instance.interceptors.response.use(
        (response) => {
            const { data } = response;
            if (Number(data?.code) !== 200) {
                switch (Number(data?.code)) {
                    case 401:
                    case 403:
                        // 清除satoken
                        tokenUtils.deleteCookie("satoken");
                        localStorage.removeItem("user-store");
                        import("@/stores").then(({ useUserStore }) => {
                            const userStore = useUserStore();
                            userStore.clearUser();
                            // 跳转到登录页
                            if (router.currentRoute?.value?.path !== "/login") {
                                router.replace({ path: "/login" });
                            }
                        });
                        const authError = new Error(
                            data?.message || "登录已过期，请重新登录",
                        );
                        authError.isAuthRedirect = true;
                        return Promise.reject(authError);
                    case 500:
                        ElMessage.error(data?.message || "服务器错误");
                        return Promise.reject(
                            new Error(data?.message || "服务器错误"),
                        );
                    default:
                        ElMessage.error(data?.message || "请求失败");
                        return Promise.reject(
                            new Error(data?.message || "请求失败"),
                        );
                }
            }
            return data;
        },
        (error) => {
            console.error("响应拦截器错误:", error);
            // 如果是认证重定向错误，避免重复提示
            if (error && error.isAuthRedirect) {
                return Promise.reject(error);
            }
            if (error.response) {
                const { status, data } = error.response;
                switch (status) {
                    case 401:
                    case 403:
                        // 清除satoken
                        tokenUtils.deleteCookie("satoken");
                        localStorage.removeItem("user-store");

                        // 清除用户store状态
                        import("@/stores").then(({ useUserStore }) => {
                            const userStore = useUserStore();
                            userStore.clearUser();
                            // 跳转到登录页
                            router.replace({
                                path: "/login",
                                // query: { redirect: router.currentRoute.value.fullPath }
                            });
                        });
                        return Promise.reject(error);
                    case 404:
                        ElMessage.error("请求的资源不存在");
                        break;
                    case 422:
                        ElMessage.error(data?.message || "请求参数验证失败");
                        break;
                    case 413:
                        ElMessage.error(
                            data?.message || "文件上传体过大，请重新上传",
                        );
                        break;
                    case 429:
                        ElMessage.error("请求过于频繁，请稍后再试");
                        break;
                    case 500:
                        ElMessage.error("服务器内部错误，请稍后重试");
                        break;
                    case 502:
                    case 503:
                    case 504:
                        ElMessage.error("服务暂时不可用，请稍后重试");
                        break;
                    default:
                        ElMessage.error(
                            data?.message || `请求失败 (${status})`,
                        );
                }
            } else if (error.code === "ECONNABORTED") {
                ElMessage.error("请求超时，请检查网络连接");
            } else if (error.message === "Network Error") {
                ElMessage.error("网络连接失败，请检查网络");
            } else {
                ElMessage.error("请求失败，请稍后重试");
            }

            return Promise.reject(error);
        },
    );

    return instance;
};

// 默认请求实例
const request = createRequest();

/**
 * 通用请求方法
 */
export const http = {
    get: (url, params, config) => request.get(url, { params, ...config }),
    post: (url, data, config) => request.post(url, data, config),
    put: (url, data, config) => request.put(url, data, config),
    patch: (url, data, config) => request.patch(url, data, config),
    delete: (url, config) => request.delete(url, config),
};

/**
 * 文件上传请求
 */
export const uploadRequest = createRequest({
    headers: {
        "Content-Type": "multipart/form-data",
    },
    timeout: 30000, // 文件上传超时时间更长
});

/**
 * 下载文件请求
 */
export const downloadRequest = createRequest({
    responseType: "blob",
    timeout: 60000, // 下载超时时间更长
});

export default request;
export { createRequest, tokenUtils };
